// Full menu, normalised from the existing menu data so there is one source of
// truth. Item ids match the legacy /Comp/:id ids where they existed; Rolls and
// Beverages (previously hidden) get stable ids from 59 upwards.
import {
  productDataRolls,
  productDatagourmet,
  productDataSundaes,
  productDataCones,
  productDatashake,
  productDatamomo,
  productDatamunchie,
  productDataSignatures,
  productDataburger,
  productDataSand,
  productDataBev,
} from "../../components/Menuall/data";
import { CombinedData } from "../../components/Menuall/CombinedData";
import bevOrange from "../assets/photos/beverages/orange.webp";
import bevStrawberry from "../assets/photos/beverages/strawberry.webp";
import bevMojito from "../assets/photos/beverages/mojito.webp";
import bevLimeLemon from "../assets/photos/beverages/lime-lemon.webp";
import bevBlueberry from "../assets/photos/beverages/blueberry.webp";
import bevJeera from "../assets/photos/beverages/jeera.webp";

// Beverages have no photography in the site data. These are AI-generated
// reference images (Higgsfield, GPT Image 2.5, 2026-10-17) keyed by item slug;
// swap them for real café photos when those are supplied.
const generatedBeveragePhotos = {
  orange: bevOrange,
  strawberry: bevStrawberry,
  mojito: bevMojito,
  "lime-and-lemon": bevLimeLemon,
  blueberry: bevBlueberry,
  jeera: bevJeera,
};

const slugify = (s) =>
  s
    .toLowerCase()
    .replace(/&/g, "and")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "");

export const menuCategories = [
  { slug: "ice-cream-rolls", name: "Ice Cream Rolls", short: "Rolls", blurb: "Hand-rolled on the cold plate", colour: "#087f83", pale: "#e3f2ee", icon: "roll", source: productDataRolls },
  { slug: "gourmet-plates", name: "Gourmet Plates", short: "Plates", blurb: "Rolls served as a dessert plate", colour: "#9a3d51", pale: "#f6e3e8", icon: "plate", source: productDatagourmet },
  { slug: "exotic-sundaes", name: "Exotic Sundaes", short: "Sundaes", blurb: "Cups loaded with toppings", colour: "#a83c27", pale: "#f8e6de", icon: "sundae", source: productDataSundaes },
  { slug: "waffle-cones", name: "Waffle Cones", short: "Cones", blurb: "Scoops in a crisp cone", colour: "#8a6200", pale: "#fff0c7", icon: "cone", source: productDataCones },
  { slug: "shakes", name: "Shakes", short: "Shakes", blurb: "Thick, cold and blended", colour: "#6b4a35", pale: "#f1e6dc", icon: "shake", source: productDatashake },
  { slug: "momos", name: "Momos", short: "Momos", blurb: "Steamed or fried, with chutney", colour: "#075b60", pale: "#e1eee6", icon: "momo", source: productDatamomo },
  { slug: "munchies", name: "Munchies", short: "Munchies", blurb: "Crisp bites to share", colour: "#8a6200", pale: "#fff3cf", icon: "munchies", source: productDatamunchie },
  { slug: "signatures", name: "Signatures", short: "Signatures", blurb: "RollOver desserts with a twist", colour: "#0b6b6e", pale: "#dff0ee", icon: "signature", source: productDataSignatures },
  { slug: "burgers", name: "Burgers", short: "Burgers", blurb: "Cheese, mayo, big bite", colour: "#a83c27", pale: "#f8e6de", icon: "burger", source: productDataburger },
  { slug: "sandwiches", name: "Sandwiches", short: "Sandwiches", blurb: "Grilled and toasted", colour: "#075b60", pale: "#e1eee6", icon: "sandwich", source: productDataSand },
  { slug: "beverages", name: "Beverages", short: "Drinks", blurb: "Chilled coolers", colour: "#087f83", pale: "#e3f7f3", icon: "bottle", source: productDataBev, placeholderMedia: true },
];

const byName = new Map(CombinedData.map((c) => [c.name.trim().toLowerCase(), c]));
let nextId = 59;

export const menuItems = [];
menuCategories.forEach((cat) => {
  cat.items = cat.source.map((raw) => {
    const name = raw.name.trim();
    // Beverages were never in the legacy data, so a name match there (e.g. the
    // "Strawberry" waffle cone) would steal another item's id.
    const legacy = cat.placeholderMedia ? null : byName.get(name.toLowerCase());
    const id = legacy ? legacy.id : nextId++;
    const veg = raw.img1 && /veg/.test(String(raw.img1)) && !/nonveg/.test(String(raw.img1));
    // Beverages in the source data reuse a sandwich photo and description; only
    // the name and price are usable, so the description is left empty and the
    // photo is a generated reference image until the café supplies photography.
    const slug = slugify(name);
    const generated = cat.placeholderMedia ? generatedBeveragePhotos[slug] || null : null;
    const item = {
      id,
      slug,
      name,
      price: raw.price.replace(/\.00$/, ""),
      desc: cat.placeholderMedia ? "" : raw.desc,
      withText: legacy && legacy.with ? legacy.with.trim() : null,
      veg: cat.placeholderMedia ? true : veg,
      photo: cat.placeholderMedia ? generated : raw.img,
      photoStatus: cat.placeholderMedia ? (generated ? "generated" : "missing") : "menu",
      categorySlug: cat.slug,
      category: cat.name,
      colour: cat.colour,
      pale: cat.pale,
    };
    menuItems.push(item);
    return item;
  });
  delete cat.source;
});

// Alt text and captions; generated images are not described as photographs.
export const photoAlt = (item) => (item.photoStatus === "generated" ? item.name : `${item.name} as photographed for the RollOver menu`);
export const photoCaption = (item) => (item.photoStatus === "generated" ? item.name : "RollOver menu photography");

export const findCategory = (slug) => menuCategories.find((c) => c.slug === slug);
export const findMenuItem = (id) => menuItems.find((i) => String(i.id) === String(id));
export const defaultCategory = menuCategories[0];
